import React, { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff, KeyRound, TriangleAlert } from 'lucide-react';
import { TagsInput, cx } from '../../../../ui';

const DAYS = [
  [1, 'Mon', 'Monday'],
  [2, 'Tue', 'Tuesday'],
  [3, 'Wed', 'Wednesday'],
  [4, 'Thu', 'Thursday'],
  [5, 'Fri', 'Friday'],
  [6, 'Sat', 'Saturday'],
  [0, 'Sun', 'Sunday'],
];

const INPUT_MODES = { Email: 'email', Url: 'url', Phone: 'tel' };

const domId = key => `set-${key.replace(/[^A-Za-z0-9]+/g, '-')}`;

/**
 * Write-only secret: the API only says whether one is set (and its last 4 characters when it's long).
 * Empty draft = remove on save; no draft = keep. Typing and then clearing the box keeps the saved one.
 */
const SecretControl = ({
  field,
  id,
  draft,
  hasDraft,
  onChange,
  onDiscard,
  describedBy,
  invalid,
}) => {
  const [replacing, setReplacing] = useState(false);
  const [visible, setVisible] = useState(false);
  const inputRef = useRef(null);
  const removing = hasDraft && draft === '';

  // After a save (or Cancel) the draft is gone: back to the summary.
  useEffect(() => {
    if (!hasDraft) {
      setReplacing(false);
      setVisible(false);
    }
  }, [hasDraft]);

  useEffect(() => {
    if (replacing) inputRef.current?.focus();
  }, [replacing]);

  if (removing) {
    return (
      <div className="adm-set-secret">
        <span className="adm-set-secret__text">Removed when you save.</span>
        <button
          type="button"
          className="pk-btn pk-btn--ghost pk-btn--xs"
          onClick={onDiscard}
        >
          Undo
        </button>
      </div>
    );
  }

  const showSummary =
    field.isSet && !field.unreadable && !replacing && !hasDraft;
  if (showSummary) {
    return (
      <div className="adm-set-secret">
        <KeyRound aria-hidden="true" />
        <span className="adm-set-secret__text" id={id}>
          <span aria-hidden="true">••••••••</span>
          {field.hint ? (
            <span className="adm-set-secret__hint">{field.hint}</span>
          ) : null}
          <span className="sr-only">
            {field.hint ? `Saved, ends in ${field.hint}` : 'Saved'}
          </span>
        </span>
        <span className="adm-set-secret__actions">
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--xs"
            onClick={() => setReplacing(true)}
          >
            Replace
          </button>
          <button
            type="button"
            className="pk-btn pk-btn--ghost pk-btn--xs"
            onClick={() => onChange('')}
          >
            Remove
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="adm-set-secret-input">
      <input
        ref={inputRef}
        id={id}
        className="pk-input"
        type={visible ? 'text' : 'password'}
        value={hasDraft ? draft : ''}
        onChange={e =>
          e.target.value === '' ? onDiscard() : onChange(e.target.value)
        }
        placeholder={
          field.isSet && !field.unreadable
            ? 'Enter the new value'
            : 'Paste the value'
        }
        maxLength={field.maxLength}
        autoComplete="new-password"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore="true"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      <button
        type="button"
        className="adm-icon-btn"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? `Hide ${field.label}` : `Show ${field.label}`}
        aria-pressed={visible}
      >
        {visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
      </button>
      {replacing && (
        <button
          type="button"
          className="pk-btn pk-btn--ghost pk-btn--xs"
          onClick={() => {
            setReplacing(false);
            onDiscard();
          }}
        >
          Cancel
        </button>
      )}
    </div>
  );
};

const DaysControl = ({ id, value, onChange, labelId }) => {
  const selected = new Set(Array.isArray(value) ? value.map(Number) : []);
  const toggle = day => {
    const next = new Set(selected);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    onChange([...next].sort((a, b) => a - b));
  };
  return (
    <div
      className="adm-set-days"
      role="group"
      aria-labelledby={labelId}
      id={id}
    >
      {DAYS.map(([day, short, long]) => (
        <button
          key={day}
          type="button"
          className="pk-chip adm-set-day"
          aria-pressed={selected.has(day)}
          aria-label={long}
          onClick={() => toggle(day)}
        >
          {short}
        </button>
      ))}
    </div>
  );
};

/** One setting: label and help on the left, the control on the right (stacked on small screens). */
const SettingControl = ({
  field,
  value,
  hasDraft,
  error,
  resetPending,
  onChange,
  onDiscard,
  onReset,
  onUndoReset,
}) => {
  const id = domId(field.key);
  const labelId = `${id}-label`;
  const helpId = field.help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);
  const isToggle = field.kind === 'Toggle';

  const common = {
    id,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
  };

  let control;
  switch (field.kind) {
    case 'Toggle':
      control = (
        <input
          {...common}
          type="checkbox"
          role="switch"
          className="switch"
          checked={Boolean(value)}
          onChange={e => onChange(e.target.checked)}
        />
      );
      break;
    case 'Secret':
      control = (
        <SecretControl
          field={field}
          id={id}
          draft={value}
          hasDraft={hasDraft}
          onChange={onChange}
          onDiscard={onDiscard}
          describedBy={describedBy}
          invalid={invalid}
        />
      );
      break;
    case 'Select': {
      const current = value == null ? '' : String(value);
      const known = field.options?.some(o => o.value === current);
      control = (
        <select
          {...common}
          className="pk-select"
          value={current}
          onChange={e => onChange(e.target.value)}
        >
          {(!field.required || !current) && (
            <option value="">{field.required ? 'Choose…' : 'Not set'}</option>
          )}
          {current && !known && <option value={current}>{current}</option>}
          {field.options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
      break;
    }
    case 'Number':
      control = (
        <input
          {...common}
          className="pk-input adm-set-number"
          type="number"
          inputMode={field.wholeNumber ? 'numeric' : 'decimal'}
          step={field.wholeNumber ? 1 : 'any'}
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
        />
      );
      break;
    case 'TextArea':
      control = (
        <textarea
          {...common}
          className="pk-textarea"
          rows={3}
          maxLength={field.maxLength}
          placeholder={field.placeholder || undefined}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
        />
      );
      break;
    case 'List':
      control = (
        <TagsInput
          id={id}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          tokenSeparators={[',', ';', ' ']}
          placeholder={field.placeholder || 'Add and press Enter'}
        />
      );
      break;
    case 'Days':
      control = (
        <DaysControl
          id={id}
          value={value}
          onChange={onChange}
          labelId={labelId}
        />
      );
      break;
    default:
      control = (
        <input
          {...common}
          className={cx('pk-input', field.uppercase && 'adm-set-upper')}
          type={
            field.format === 'Email'
              ? 'email'
              : field.format === 'Url'
                ? 'url'
                : 'text'
          }
          inputMode={INPUT_MODES[field.format]}
          maxLength={field.maxLength}
          placeholder={field.placeholder || undefined}
          autoComplete="off"
          spellCheck={field.format === 'None' && field.maxLength > 60}
          value={value ?? ''}
          onChange={e =>
            onChange(
              field.uppercase ? e.target.value.toUpperCase() : e.target.value
            )
          }
        />
      );
  }

  let meta = null;
  if (resetPending) {
    meta = (
      <>
        <span>Goes back to the server value or default when you save.</span>
        <button type="button" className="adm-set-link" onClick={onUndoReset}>
          Undo
        </button>
      </>
    );
  } else if (field.source === 'saved' && !hasDraft) {
    meta = (
      <button
        type="button"
        className="adm-set-link"
        onClick={onReset}
        aria-label={`Reset ${field.label} to the server value or default`}
      >
        Reset to default
      </button>
    );
  } else if (field.source === 'server' && !hasDraft) {
    meta = <span>From the server configuration</span>;
  }

  return (
    <div
      className={cx(
        'adm-set-row',
        isToggle && 'adm-set-row--toggle',
        hasDraft && 'is-edited',
        invalid && 'has-error'
      )}
    >
      <div className="adm-set-row__label">
        {field.kind === 'Days' || field.kind === 'Secret' ? (
          <span className="adm-set-row__title" id={labelId}>
            {field.kind === 'Secret' ? (
              <label htmlFor={id}>{field.label}</label>
            ) : (
              field.label
            )}
          </span>
        ) : (
          <label className="adm-set-row__title" htmlFor={id} id={labelId}>
            {field.label}
            {field.required && field.kind !== 'Select' && (
              <span className="sr-only"> (required)</span>
            )}
          </label>
        )}
        {field.help && (
          <p className="adm-set-row__help" id={helpId}>
            {field.help}
          </p>
        )}
      </div>
      <div className="adm-set-row__control">
        {control}
        {field.unreadable && !hasDraft && (
          <p className="adm-set-row__warn">
            <TriangleAlert aria-hidden="true" />
            The saved value can't be read because the encryption key changed.
            Enter it again.
          </p>
        )}
        {error && (
          <p className="adm-set-row__error" id={errorId} role="alert">
            {error}
          </p>
        )}
        {meta && <div className="adm-set-row__meta">{meta}</div>}
      </div>
    </div>
  );
};

export default SettingControl;
